export type BrandId = 'republic' | 'verdant'

export interface BrandImages {
  hero: string
  clinician: string
  labKit: string
  /** Per-program overrides, keyed by program slug. Falls back to the shared data. */
  programs: Record<string, string>
  /** Per-product overrides, keyed by product slug. Falls back to the shared data. */
  products: Record<string, string>
}

export interface BrandCopy {
  utilityLeft: string
  utilityRight: string
  heroTitle: [string, string]
  heroSubhead: string
  manifesto: string
  ctaIntro: string
  metaTitle: string
  metaDescription: string
  legalName: string
}

export interface Brand {
  id: BrandId
  /** Display name, substituted anywhere shared copy references the operator. */
  name: string
  /** Wordmark treatment differs per brand: Republic is letterspaced caps, Verdant is title case. */
  wordmark: string
  wordmarkCase: 'upper' | 'title'
  monogram: string
  sealTop: string
  sealBottom: string
  mark: 'ring' | 'leaf'
  copy: BrandCopy
  images: BrandImages
}

const REPUBLIC_PRODUCTS = {
  tirzepatide:
    'https://cdn.magicpatterns.com/patterns/generated-images/893aa786-99c9-47b9-8dd4-8fe1f7bc6c46.jpg',
}

export const brands: Record<BrandId, Brand> = {
  republic: {
    id: 'republic',
    name: 'Republic',
    wordmark: 'REPUBLIC',
    wordmarkCase: 'upper',
    monogram: 'R',
    sealTop: 'ACCESS · TRANSPARENCY',
    sealBottom: 'CONTROL · CHOICE',
    mark: 'ring',
    copy: {
      utilityLeft: 'Clinically directed care · Licensed in all 50 states',
      utilityRight: 'Membership from $39/mo · Price held 12 months',
      heroTitle: ['Healthcare belongs', 'to everyone.'],
      heroSubhead:
        'Clinically directed care and medications delivered with exceptional quality, convenience, transparency and pricing.',
      manifesto: 'More choice is better health for all.',
      ctaIntro:
        'Start with the intake. Nothing is charged until a clinician has reviewed your labs and you have approved the protocol.',
      metaTitle: 'Republic — Healthcare belongs to everyone.',
      metaDescription:
        'Clinically directed care and medications delivered with exceptional quality, convenience, transparency and pricing.',
      legalName: 'Republic Health, Inc.',
    },
    images: {
      hero: 'https://cdn.magicpatterns.com/patterns/generated-images/34e0720d-d3f4-4fc7-b5b4-ead07b2abfce.jpg',
      clinician:
        'https://cdn.magicpatterns.com/patterns/generated-images/2e100bad-ad12-4fe5-bf93-f579c92d013a.jpg',
      labKit:
        'https://cdn.magicpatterns.com/patterns/generated-images/e729c5dc-755b-4a47-bb49-2404bfa42c4e.jpg',
      programs: {},
      products: REPUBLIC_PRODUCTS,
    },
  },

  verdant: {
    id: 'verdant',
    name: 'Verdant',
    wordmark: 'Verdant',
    wordmarkCase: 'title',
    monogram: 'V',
    sealTop: 'CARE · CLARITY',
    sealBottom: 'TRUST · CONTINUITY',
    mark: 'leaf',
    copy: {
      utilityLeft: 'Clinician-led care · Licensed in all 50 states',
      utilityRight: 'Membership from $39/mo · Cancel any time',
      heroTitle: ['Care that grows', 'with you.'],
      heroSubhead:
        'Hormone, metabolic and longevity care led by licensed clinicians, delivered to your door with pricing you can see before you start.',
      manifesto: 'Better health, one steady step at a time.',
      ctaIntro:
        'Begin with a short intake. Nothing is charged until a clinician reviews your labs and you approve the plan.',
      metaTitle: 'Verdant — Care that grows with you.',
      metaDescription:
        'Clinician-led hormone, metabolic and longevity care delivered to your door, with lab work and pricing you can see up front.',
      legalName: 'Verdant Health, Inc.',
    },
    images: {
      hero: 'https://cdn.magicpatterns.com/patterns/generated-images/589a81df-684d-4c7b-a1bc-bb64b0951af3.jpg',
      clinician:
        'https://cdn.magicpatterns.com/patterns/generated-images/e206c78e-8a2e-407e-bde1-b495615e4dc8.jpg',
      labKit:
        'https://cdn.magicpatterns.com/patterns/generated-images/13a34ce5-9abc-49d9-9f92-06e664abf0d8.jpg',
      programs: {
        'hormone-optimization-men':
          'https://cdn.magicpatterns.com/patterns/generated-images/88cce9fe-671a-4475-9020-761739a29759.jpg',
        'hormone-therapy-women':
          'https://cdn.magicpatterns.com/patterns/generated-images/589a81df-684d-4c7b-a1bc-bb64b0951af3.jpg',
        'weight-management':
          'https://cdn.magicpatterns.com/patterns/generated-images/e69dfb81-27b2-49c1-9dde-376397d68d10.jpg',
        'longevity-peptides':
          'https://cdn.magicpatterns.com/patterns/generated-images/4c05bcc7-e3f0-45f8-b3e3-ac244d949797.jpg',
        'sexual-health':
          'https://cdn.magicpatterns.com/patterns/generated-images/88cce9fe-671a-4475-9020-761739a29759.jpg',
        'hair-and-skin':
          'https://cdn.magicpatterns.com/patterns/generated-images/589a81df-684d-4c7b-a1bc-bb64b0951af3.jpg',
      },
      products: {
        tirzepatide:
          'https://cdn.magicpatterns.com/patterns/generated-images/e69dfb81-27b2-49c1-9dde-376397d68d10.jpg',
        semaglutide:
          'https://cdn.magicpatterns.com/patterns/generated-images/e69dfb81-27b2-49c1-9dde-376397d68d10.jpg',
        'testosterone-cypionate':
          'https://cdn.magicpatterns.com/patterns/generated-images/03596fa1-fd5d-4663-a4d7-95973fc0b463.jpg',
        'estradiol-progesterone':
          'https://cdn.magicpatterns.com/patterns/generated-images/0ba6f077-6b1e-4635-835e-ba9dba0b6ac2.jpg',
        tadalafil:
          'https://cdn.magicpatterns.com/patterns/generated-images/7108f166-7ad3-44fa-8b3e-d2ed7f6d8e1b.jpg',
        'finasteride-minoxidil':
          'https://cdn.magicpatterns.com/patterns/generated-images/9e292996-4a4b-4bd4-9eca-feda2a4167fb.jpg',
        tesamorelin:
          'https://cdn.magicpatterns.com/patterns/generated-images/4c05bcc7-e3f0-45f8-b3e3-ac244d949797.jpg',
        'bpc-157':
          'https://cdn.magicpatterns.com/patterns/generated-images/4c05bcc7-e3f0-45f8-b3e3-ac244d949797.jpg',
        'nad-plus':
          'https://cdn.magicpatterns.com/patterns/generated-images/03596fa1-fd5d-4663-a4d7-95973fc0b463.jpg',
        enclomiphene:
          'https://cdn.magicpatterns.com/patterns/generated-images/7108f166-7ad3-44fa-8b3e-d2ed7f6d8e1b.jpg',
      },
    },
  },
}
